import React, { useState, useEffect, useRef } from 'react';
import { AnalysisData, OffreData } from '../../types/index';
import { GaugeChart } from './GaugeChart';

interface FinancialSummaryProps {
  analysis: AnalysisData;
  offreData: OffreData;
  tauxCapitalisation: number;
  onTauxCapitalisationChange: (v: number) => void;
  onPrixConseilleChange: (prix: number) => void;
}

const fmt = (v: number) =>
  new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 0 }).format(v);

const Tooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="relative group inline-flex ml-1">
    <span className="w-4 h-4 rounded-full border border-gray-400 dark:border-gray-500 text-gray-400 dark:text-gray-500 text-[10px] font-bold flex items-center justify-center cursor-help hover:border-blue-500 hover:text-blue-500 transition-colors">
      ?
    </span>
    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded-lg shadow-lg whitespace-normal w-56 text-left opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
      {text}
      <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800 dark:border-t-gray-700" />
    </span>
  </span>
);

export const FinancialSummary: React.FC<FinancialSummaryProps> = ({
  analysis,
  offreData,
  tauxCapitalisation,
  onTauxCapitalisationChange,
  onPrixConseilleChange,
}) => {
  const [priceInput, setPriceInput] = useState(fmt(offreData.offreConseillee));
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setPriceInput(fmt(offreData.offreConseillee));
    }
  }, [offreData.offreConseillee, isEditing]);

  const commitPrice = () => {
    const raw = priceInput.replace(/[^0-9]/g, '');
    const prix = parseInt(raw, 10);
    setIsEditing(false);
    if (prix > 0) {
      onPrixConseilleChange(prix);
    } else {
      setPriceInput(fmt(offreData.offreConseillee));
    }
  };

  return (
    <div>
      <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
        <span className="text-yellow-500">&#9733;</span>
        Analyse n&deg; 1: Analyse initiale
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Acquisition et objectifs — basé sur l'offre conseillée */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">
            Acquisition et objectifs
          </h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Offre conseill&eacute;e</div>
              <div className="flex items-baseline gap-1">
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  onFocus={() => {
                    setIsEditing(true);
                    setPriceInput(String(Math.round(offreData.offreConseillee)));
                    setTimeout(() => inputRef.current?.select(), 0);
                  }}
                  onBlur={commitPrice}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.currentTarget.blur(); }
                    if (e.key === 'Escape') { setIsEditing(false); setPriceInput(fmt(offreData.offreConseillee)); e.currentTarget.blur(); }
                  }}
                  className="w-28 text-xl font-bold text-green-600 dark:text-green-400 bg-transparent border-b-2 border-transparent focus:border-green-500 focus:outline-none text-right"
                  title="Cliquer pour saisir un montant précis"
                />
                <span className="text-sm text-gray-500">CHF</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Par m&sup2;</div>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  {fmt(offreData.prixM2Conseille)}
                </span>
                <span className="text-sm text-gray-500">CHF/m&sup2;</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center">
                Rendement
                <Tooltip text="Rendement net sur l'offre conseill&eacute;e : revenu net / offre conseill&eacute;e. &Eacute;gal au taux de capitalisation par construction." />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  {offreData.rendementNet.toFixed(1)}%
                </span>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center">
                ROE
                <Tooltip text="Return on Equity sur l'offre conseill&eacute;e : cash-flow annuel / apport. Mesure la rentabilit&eacute; r&eacute;elle de votre argent si vous achetez &agrave; l'offre conseill&eacute;e." />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  {offreData.roe.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Taux de capitalisation slider */}
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                Taux de capitalisation:
                <Tooltip text="Param&egrave;tre de valorisation : le rendement brut que vous ciblez. L'offre conseill&eacute;e est calcul&eacute;e en divisant le revenu brut annuel (loy&eacute;) par ce taux." />
              </span>
              <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                {tauxCapitalisation.toFixed(1)}%
              </span>
            </div>
            <input
              type="range"
              min="1"
              max="15"
              step="0.1"
              value={tauxCapitalisation}
              onChange={(e) => onTauxCapitalisationChange(parseFloat(e.target.value))}
              className="w-full h-2 bg-blue-200 dark:bg-blue-900 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
        </div>

        {/* Situation */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">
            Situation
          </h3>
          <div className="flex items-center justify-around">
            <div className="text-center">
              <GaugeChart
                value={analysis.emplacementScore}
                label="Emplacement"
                color="#10B981"
                subtitle="A venir"
              />
              <div className="mt-2 text-2xl font-bold text-gray-800 dark:text-gray-200">
                {analysis.emplacementScore.toFixed(1)}
                <span className="text-sm text-gray-400 font-normal">/5</span>
              </div>
            </div>
            <div className="text-center">
              <GaugeChart
                value={analysis.etatGeneral}
                label="Etat g&eacute;n&eacute;ral"
                color="#6366F1"
              />
              <div className="mt-2 text-2xl font-bold text-gray-800 dark:text-gray-200">
                {analysis.etatGeneral.toFixed(1)}
                <span className="text-sm text-gray-400 font-normal">/5</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
